import type { QuoteData } from '@/types/quote.types';
import type { IRequestParams } from '../frontBack';
import type { getWidgetAgentData, getWidgetSellerIdentity } from '@/utils/widgetAgent';

type AgentData = ReturnType<typeof getWidgetAgentData>;
type SellerIdentity = ReturnType<typeof getWidgetSellerIdentity>;
type CotizacionData = NonNullable<QuoteData['cotizacionData']>;
type PersonalData = QuoteData['personalData'];
type ExtraParams = Record<string, unknown>;

const when = (condition: unknown, value: ExtraParams): ExtraParams =>
  condition ? value : {};

const calcularEdad = (fechaNacimiento: string | null | undefined): number => {
  if (!fechaNacimiento) return 0;
  const fecha = new Date(fechaNacimiento);
  const hoy = new Date();
  let edad = hoy.getFullYear() - fecha.getFullYear();
  const mes = hoy.getMonth() - fecha.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < fecha.getDate())) {
    edad--;
  }
  return edad;
};

// Gender to Id_Sexo (1 = M, 2 = F)
const getIdSexo = (gender: string | null | undefined): number => {
  if (gender === 'M') return 1;
  if (gender === 'F') return 2;
  return 0;
};

const vehicleParams = (quoteData: QuoteData): ExtraParams => ({
  // Marca must be the ID (numeric string), not the name
  Modelo: quoteData.year ? String(quoteData.year) : '',
  Marca: quoteData.brand?.id ? String(quoteData.brand.id) : '',
  Tipo: quoteData.type || '',
  Clave_Qualitas: quoteData.amis || '',
  Clave_Ana: '0',
});

const coverageParams = (cotizacionData: CotizacionData): ExtraParams => ({
  // Defaults: Contado and Amplia only
  Id_Aut_Colectividad: 1,
  Cias: '2,3',
  Paquetes: '1',
  Formas_Pago: 'C',
  Descuento_Ana: Number(cotizacionData.descuentoAna ?? 40),
  Descuento_Qualitas: Number(cotizacionData.descuentoQualitas ?? 45),
  Deducible_DM: Number(cotizacionData.deducibleDM ?? 5),
  Deducible_Robo: Number(cotizacionData.deducibleRobo ?? 10),
  // SA_RC and SA_GMO are sent only when explicitly defined, to avoid
  // exceeding authorized limits with default values
  ...when(cotizacionData.saRC !== undefined, { SA_RC: Number(cotizacionData.saRC) }),
  ...when(cotizacionData.saGMO !== undefined, { SA_GMO: Number(cotizacionData.saGMO) }),
});

const locationParams = (personalData: PersonalData): ExtraParams => ({
  ...when(personalData?.idEstado, { Id_Estado: personalData?.idEstado }),
  ...when(personalData?.idCiudad, { Id_Ciudad: personalData?.idCiudad }),
  ...when(personalData?.idMunicipio, { Id_Municipio: personalData?.idMunicipio }),
});

const contactParams = (personalData: PersonalData): ExtraParams => ({
  Id_Sexo: getIdSexo(personalData?.gender),
  Email_Prospecto: personalData?.email || '',
  Telefono_Prospecto: personalData?.whatsapp || '',
  Edad: calcularEdad(personalData?.birthDate),
});

const prospectParams = (
  quoteData: QuoteData,
  cotizacionData: CotizacionData
): ExtraParams => {
  const personalData = quoteData.personalData;
  return {
    Nombre_Prospecto: personalData?.fullName || cotizacionData.nombreAsegurado || '',
    Codigo_Postal: personalData?.postalCode || quoteData.postalCode || '',
    ...contactParams(personalData),
    ...locationParams(personalData),
  };
};

const sellerParams = (agentData: AgentData, seller: SellerIdentity | null): ExtraParams => ({
  ...when(agentData.Id_Persona !== null, { Id_Persona_Vendedor: agentData.Id_Persona }),
  ...when(seller?.Guid_Vendedor, { Guid_Vendedor: seller?.Guid_Vendedor }),
  ...when(seller?.Clave_Agente, { Clave_Agente: seller?.Clave_Agente }),
});

const updateParams = (cotizacionData: CotizacionData): ExtraParams => ({
  ...when(cotizacionData.idCotizacionAutosDetalle, {
    Id_Cotizacion_Autos_Detalle: cotizacionData.idCotizacionAutosDetalle,
  }),
  ...when(cotizacionData.idCotizacion, { Id_Cotizacion: cotizacionData.idCotizacion }),
});

export interface DetalleParamsInput {
  quoteData: QuoteData;
  isUpdate: boolean;
  agentData: AgentData;
  /** Seller identity; only used (and only read) on Insert. */
  seller: SellerIdentity | null;
}

export const buildDetalleParams = (input: DetalleParamsInput): IRequestParams => {
  const { quoteData, isUpdate, agentData, seller } = input;
  const cotizacionData = quoteData.cotizacionData || {};
  const usuario = agentData.Id_Usuario;

  const params: ExtraParams = {
    ClassName: 'cotizaciones_autos_detalle',
    Action: isUpdate ? 'Update' : 'Insert',
    db: 'Prometeo',
    UsuarioAdd: usuario,
    UsuarioUmod: usuario,
    UsuarioDel: usuario,
    Id_Usuario_Modificacion: agentData.Id_Usuario_Modificacion,
    ...vehicleParams(quoteData),
    ...coverageParams(cotizacionData),
    ...prospectParams(quoteData, cotizacionData),
    Para: '',
    Subject: '',
    Body: '',
    Descripcion: '',
    Id_Cotizaciones_Campania: 3,
    // Privacy notice consent (1 = true, 0 = false)
    Consentimiento_Privacidad: quoteData.personalData?.privacyConsent ? 1 : 0,
    ...sellerParams(agentData, seller),
    ...(isUpdate ? updateParams(cotizacionData) : {}),
    // Id_Ticket applies to both Insert and Update
    ...when(cotizacionData.idTicket, { Id_Ticket: cotizacionData.idTicket }),
  };
  return params as IRequestParams;
};
